import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Pagination,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LIDA_POR_ABA,
  NOTIFICACOES_RESUMO_INICIAL,
  useEstadoNotificacoes,
  useNotificacoes,
  useNotificacoesResumo,
  usePreferenciasNotificacoes,
} from '../../hooks/useNotificacoes';
import { useArrastarParaFechar } from '../../hooks/useArrastarParaFechar';
import { useFecharNoVoltar } from '../../hooks/useFecharNoVoltar';
import { MOBILE } from '../../theme';
import { isUserAdmin } from '../../utils/auth';
import NotificationFilters from './NotificationFilters';
import NotificationItem from './NotificationItem';

const EMPTY_MESSAGES = {
  todas: 'Nenhuma notificação por aqui.',
  'nao-lidas': 'Nenhuma notificação não lida.',
  lidas: 'Nenhuma notificação lida ainda.',
};

/**
 * Gaveta lateral com histórico, filtros, paginação e preferência de e-mail.
 *
 * Aba e categoria vêm do backend (`NotificacaoEstado`), iguais à central e aos
 * outros dispositivos; só a página atual é local.
 */
const NotificationDrawer = ({ open, onClose, centralPath = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  // Gestos nativos do celular: o botão voltar e o arrasto para a direita
  // fecham a gaveta (ver docs/MOBILE.md).
  useFecharNoVoltar(open, onClose);

  // A saída é SEMPRE instantânea: o gesto já anima o painel e, com duração 0,
  // o `Modal` desmonta no mesmo frame (sem `ModalRoot` cobrindo a tela).
  //
  // Antes a duração 0 valia só no fechamento por gesto e voltava ao padrão do MUI
  // na reabertura. Trocar `transitionDuration` no meio do ciclo deixa o `Modal`
  // com `exited = false`: o `ModalRoot` (position: fixed; inset: 0, z-index acima
  // do header) fica montado e invisível, engolindo todos os cliques — era isso
  // que impedia a gaveta de reabrir pelo sino depois de um swipe.
  const fecharPorGesto = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const arrasto = useArrastarParaFechar({
    aberto: open,
    onFechar: fecharPorGesto,
    direcao: 'direita',
    limiteRelativo: MOBILE.dismissRatio,
  });

  const { resumo = NOTIFICACOES_RESUMO_INICIAL } = useNotificacoesResumo?.() || {};
  const { estado, isLoading: isLoadingEstado, salvar: salvarEstado } = useEstadoNotificacoes();
  const filter = estado.aba;
  const categoria = estado.categoria || '';
  const { notificacoes, isLoading, marcarLida, marcarTodas, limparLidas, remover, pagination } =
    useNotificacoes({
      lida: LIDA_POR_ABA[filter],
      categoria,
      page,
      limit: estado.itensPorPagina,
      enabled: !isLoadingEstado,
    });
  const { preferencias, atualizar } = usePreferenciasNotificacoes();

  // A aba/categoria podem mudar por tempo real (outro dispositivo): volta para a
  // primeira página para não ficar preso em uma página que não existe mais.
  useEffect(() => {
    setPage(1);
  }, [filter, categoria]);

  const handleOpen = (notificacao) => {
    if (!notificacao.lida) marcarLida.mutate({ id: notificacao.id, lida: true });

    if (notificacao.acao?.url) {
      onClose?.();
      navigate(notificacao.acao.url);
    }
  };

  const handleFilterChange = (aba) => {
    setPage(1);
    salvarEstado.mutate({ aba });
  };

  const handleCategoria = (valor) => {
    // Clicar na categoria já ativa volta para "todas as categorias".
    const proxima = valor && valor === categoria ? null : valor || null;

    setPage(1);
    salvarEstado.mutate({ categoria: proxima });
  };

  const handleOpenCenter = () => {
    onClose?.();
    navigate(centralPath || (isUserAdmin() ? '/notificacoes' : '/user/notificacoes'));
  };

  const handleToggleEmail = (event) => {
    atualizar.mutate({ emailAtivo: event.target.checked });
  };

  const total = pagination?.total ?? notificacoes.length;
  const totalGeral = resumo.total || total;
  const counts = {
    total: totalGeral,
    naoLidas: resumo.naoLidas ?? 0,
    lidas: resumo.lidas ?? 0,
  };
  const pages = pagination?.pages ?? 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Duração fixa: nunca interpolar `transitionDuration` durante a transição
      // (ver `fecharPorGesto`) — o MUI não suporta e o Modal fica preso.
      transitionDuration={{ enter: 0, exit: 0 }}
      ModalProps={{ keepMounted: false }}
      // Precisa ficar acima do header fixo do mobile (zIndex 1300 em
      // UserLayoutBrand/Layout) e do FAB de ajuda (1250); sem isso o cabeçalho
      // da gaveta — fechar, marcar todas, limpar lidas — ficava escondido.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100vw',
          height: { xs: '100dvh', sm: '100%' },
          '@supports not (height: 100dvh)': { height: { xs: '100vh' } },
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overscrollBehavior: 'contain',
          // A rolagem vertical da lista continua nativa; o arrasto horizontal
          // fica com o `useArrastarParaFechar` (também no iOS).
          touchAction: 'pan-y',
        },
      }}
    >
      <Box
        {...arrasto.handlers}
        ref={arrasto.ref}
        data-testid="notificacoes-gaveta"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          touchAction: 'pan-y',
          ...arrasto.style,
        }}
      >
        {/* Alça de arrasto: sem um indício visual o gesto não se anuncia. */}
        {isMobile && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              left: 6,
              top: '50%',
              width: 4,
              height: 56,
              transform: 'translateY(-50%)',
              borderRadius: '50px',
              bgcolor: 'divider',
              zIndex: 2,
            }}
          />
        )}
        <Box
          component="p"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          Deslize para a direita ou use o botão voltar para fechar as notificações.
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 1rem)', sm: 1.5 },
            pb: { xs: 1.2, sm: 1.5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Notificações
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalGeral} no total
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {resumo.naoLidas ?? 0} não lidas · {resumo.lidas ?? 0} lidas
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {!isMobile && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Marcar todas como lidas" arrow>
                  <span>
                    <IconButton
                      aria-label="Marcar todas como lidas"
                      onClick={() => marcarTodas.mutate()}
                      disabled={marcarTodas.isPending || resumo.naoLidas === 0}
                    >
                      <DoneAllIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Limpar notificações lidas" arrow>
                  <span>
                    <IconButton
                      aria-label="Limpar notificações lidas"
                      onClick={() => limparLidas.mutate()}
                      disabled={limparLidas.isPending || resumo.lidas === 0}
                    >
                      <DeleteSweepIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}

            <IconButton
              aria-label="Fechar notificações"
              onClick={onClose}
              sx={{ width: 40, height: 40 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* No mobile as ações em lote ganham rótulos (ícone sozinho não é
          descobrível em toque). Renderizadas só no mobile para não duplicar o
          mesmo nome acessível no DOM. */}
        {isMobile && (
          <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1.2 }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<DoneAllIcon />}
              aria-label="Marcar todas como lidas"
              onClick={() => marcarTodas.mutate()}
              disabled={marcarTodas.isPending || resumo.naoLidas === 0}
              sx={{ minHeight: 42, textTransform: 'none', fontWeight: 700 }}
            >
              Marcar todas
            </Button>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<DeleteSweepIcon />}
              aria-label="Limpar notificações lidas"
              onClick={() => limparLidas.mutate()}
              disabled={limparLidas.isPending || resumo.lidas === 0}
              sx={{ minHeight: 42, textTransform: 'none', fontWeight: 700 }}
            >
              Limpar lidas
            </Button>
          </Stack>
        )}

        <NotificationFilters
          variant="drawer"
          aba={filter}
          categoria={categoria}
          counts={counts}
          countsPorCategoria={resumo.porCategoria}
          onAbaChange={handleFilterChange}
          onCategoriaChange={handleCategoria}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            // Precisa repetir o `pan-y` AQUI: o `touch-action` não é herdado e
            // o valor efetivo é a interseção da cadeia até o container que rola.
            // Sem isso o Chrome trata o arrasto horizontal como rolagem, dispara
            // `pointercancel` e o swipe para fechar a gaveta morre no meio.
            touchAction: 'pan-y',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={26} />
            </Box>
          )}

          {!isLoading && notificacoes.length === 0 && (
            <Stack alignItems="center" spacing={1} sx={{ py: 8, color: 'text.secondary', px: 2 }}>
              <NotificationsOffOutlinedIcon fontSize="large" />
              <Typography variant="body2" textAlign="center">
                {EMPTY_MESSAGES[filter] || EMPTY_MESSAGES.todas}
              </Typography>
            </Stack>
          )}

          {!isLoading &&
            notificacoes.map((notificacao) => (
              <NotificationItem
                key={notificacao.id}
                notificacao={notificacao}
                onOpen={handleOpen}
                onMarkRead={(id) => marcarLida.mutate({ id, lida: true })}
                onMarkUnread={(id) => marcarLida.mutate({ id, lida: false })}
                onRemove={(id) => remover.mutate(id)}
              />
            ))}

          {!isLoading && pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Pagination
                size="small"
                color="primary"
                count={pages}
                page={page}
                onChange={(_event, value) => setPage(value)}
              />
            </Box>
          )}
        </Box>

        <Divider />

        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 1.5 },
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenCenter}
            sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700 }}
          >
            Abrir central de notificações
          </Button>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Receber por e-mail
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Eventos importantes (pedidos, arquivos e seleção).
              </Typography>
            </Box>
            <Switch
              checked={preferencias.emailAtivo !== false}
              onChange={handleToggleEmail}
              inputProps={{ 'aria-label': 'Receber notificações por e-mail' }}
              disabled={atualizar.isPending}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default NotificationDrawer;
